import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _toArray from "@babel/runtime/helpers/esm/toArray";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _typeof from "@babel/runtime/helpers/esm/typeof";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import warning from "rc-util/es/warning";
import { toArray } from './commonUtil';

function getKey(data, index) {
  var key = data.key;
  var value;

  if ('value' in data) {
    value = data.value;
  }

  if (key !== null && key !== undefined) {
    return key;
  }

  if (value !== undefined) {
    return value;
  }

  return "rc-index-key-".concat(index);
}

export function fillFieldNames(fieldNames) {
  var _ref = fieldNames || {},
      label = _ref.label,
      value = _ref.value,
      options = _ref.options;

  return {
    label: label || 'label',
    value: value || 'value',
    options: options || 'options'
  };
}
/**
 * Flat options into flatten list.
 * We use `optionOnly` here is aim to avoid user use nested option group.
 * Here is simply set `key` to the index if not provided.
 */

export function flattenOptions(options) {
  var _ref2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      fieldNames = _ref2.fieldNames;

  var flattenList = [];

  var _fillFieldNames = fillFieldNames(fieldNames),
      fieldLabel = _fillFieldNames.label,
      fieldValue = _fillFieldNames.value,
      fieldOptions = _fillFieldNames.options;

  function dig(list, isGroupOption) {
    list.forEach(function (data) {
      var label = data[fieldLabel];

      if (isGroupOption || !(fieldOptions in data)) {
        // Option
        flattenList.push({
          key: getKey(data, flattenList.length),
          groupOption: isGroupOption,
          data: data,
          label: label,
          value: data[fieldValue]
        });
      } else {
        // Option Group
        flattenList.push({
          key: getKey(data, flattenList.length),
          group: true,
          data: data,
          label: label
        });
        dig(data[fieldOptions], true);
      }
    });
  }

  dig(options, false);
  return flattenList;
}
/**
 * Inject `props` into `option` for legacy usage
 */

function injectPropsWithOption(option) {
  var newOption = _objectSpread({}, option);

  if (!('props' in newOption)) {
    Object.defineProperty(newOption, 'props', {
      get: function get() {
        warning(false, 'Return type is option instead of Option instance. Please read value directly instead of reading from `props`.');
        return newOption;
      }
    });
  }

  return newOption;
}

export function findValueOption(values, options) {
  var _ref3 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
      _ref3$prevValueOption = _ref3.prevValueOptions,
      prevValueOptions = _ref3$prevValueOption === void 0 ? [] : _ref3$prevValueOption;

  var optionMap = new Map();
  options.forEach(function (_ref4) {
    var data = _ref4.data,
        group = _ref4.group,
        value = _ref4.value;

    if (!group) {
      // Check if match
      optionMap.set(value, data);
    }
  });
  return values.map(function (val) {
    var option = optionMap.get(val); // Fallback to try to find prev options

    if (!option) {
      option = _objectSpread({}, prevValueOptions.find(function (opt) {
        return opt._INTERNAL_OPTION_VALUE_ === val;
      }));
    }

    return injectPropsWithOption(option);
  });
}
export var getLabeledValue = function getLabeledValue(value, _ref5) {
  var options = _ref5.options,
      prevValueMap = _ref5.prevValueMap,
      labelInValue = _ref5.labelInValue,
      optionLabelProp = _ref5.optionLabelProp;
  var item = findValueOption([value], options)[0];
  var result = {
    value: value,
    title: item === null || item === void 0 ? void 0 : item.title
  };
  var prevValItem = labelInValue ? prevValueMap.get(value) : undefined;

  if (prevValItem && _typeof(prevValItem) === 'object' && 'label' in prevValItem) {
    result.label = prevValItem.label;

    if (item && typeof prevValItem.label === 'string' && typeof item[optionLabelProp] === 'string' && prevValItem.label.trim() !== item[optionLabelProp].trim()) {
      warning(false, '`label` of `value` is not same as `label` in Select options.');
    }
  } else if (item && optionLabelProp in item) {
    result.label = item[optionLabelProp];
  } else {
    result.label = value;
    result.isCacheable = true;
  } // Used for motion control


  result.key = result.value; // 兼容组件库级联的选项自定义title

  if (item.__rc_cascader_value__ && item.node) {
    result.title = item.node.title;
  }

  return result;
};

function toRawString(content) {
  return toArray(content).join('');
}
/** Filter single option if match the search text */


function getFilterFunction(optionFilterProp) {
  return function (searchValue, option) {
    var lowerSearchText = searchValue.toLowerCase(); // Group label search

    if ('options' in option) {
      return toRawString(option.label).toLowerCase().includes(lowerSearchText);
    } // Option value search


    var rawValue = option[optionFilterProp];
    var value = toRawString(rawValue).toLowerCase();
    return value.includes(lowerSearchText);
  };
}
/** Filter options and return a new options by the search text */


export function filterOptions(searchValue, options, _ref6) {
  var optionFilterProp = _ref6.optionFilterProp,
      filterOption = _ref6.filterOption;
  var filteredOptions = [];
  var filterFunc;

  if (filterOption === false) {
    return _toConsumableArray(options);
  }

  if (typeof filterOption === 'function') {
    filterFunc = filterOption;
  } else {
    filterFunc = getFilterFunction(optionFilterProp);
  }

  options.forEach(function (item) {
    // Group should check child options
    if ('options' in item) {
      // Check group first
      var matchGroup = filterFunc(searchValue, item);

      if (matchGroup) {
        filteredOptions.push(item);
      } else {
        // Check option
        var subOptions = item.options.filter(function (subItem) {
          return filterFunc(searchValue, subItem);
        });

        if (subOptions.length) {
          filteredOptions.push(_objectSpread(_objectSpread({}, item), {}, {
            options: subOptions
          }));
        }
      }

      return;
    }

    if (filterFunc(searchValue, injectPropsWithOption(item))) {
      filteredOptions.push(item);
    }
  });
  return filteredOptions;
} // 全匹配的选项靠前

export function sortByFullMatch(a, b, mergedSearchValue, optionFilterProp) {
  var _a$optionFilterProp, _b$optionFilterProp;

  var aLabel = String((_a$optionFilterProp = a[optionFilterProp]) !== null && _a$optionFilterProp !== void 0 ? _a$optionFilterProp : '');
  var bLabel = String((_b$optionFilterProp = b[optionFilterProp]) !== null && _b$optionFilterProp !== void 0 ? _b$optionFilterProp : '');
  var searchLower = mergedSearchValue.toLowerCase();
  var aLower = aLabel.toLowerCase();
  var bLower = bLabel.toLowerCase(); // 1. 完全匹配优先

  var aExact = aLower === searchLower;
  var bExact = bLower === searchLower;
  if (aExact !== bExact) return aExact ? -1 : 1; // 2. 前缀匹配优先

  var aPrefix = aLower.startsWith(searchLower);
  var bPrefix = bLower.startsWith(searchLower);
  if (aPrefix !== bPrefix) return aPrefix ? -1 : 1; // 3. 匹配位置靠前优先

  var aIndex = aLower.indexOf(searchLower);
  var bIndex = bLower.indexOf(searchLower);
  if (aIndex !== bIndex) return aIndex - bIndex; // 4. 长度短的优先

  return aLabel.length - bLabel.length;
}
export function getSeparatedContent(text, tokens) {
  if (!tokens || !tokens.length) {
    return null;
  }

  var match = false;

  function separate(str, _ref7) {
    var _ref8 = _toArray(_ref7),
        token = _ref8[0],
        restTokens = _ref8.slice(1);

    if (!token) {
      return [str];
    }

    var list = str.split(token);
    match = match || list.length > 1;
    return list.reduce(function (prevList, unitStr) {
      return [].concat(_toConsumableArray(prevList), _toConsumableArray(separate(unitStr, restTokens)));
    }, []).filter(function (unit) {
      return unit;
    });
  }

  var list = separate(text, tokens);
  return match ? list : null;
}
export function isValueDisabled(value, options) {
  var option = findValueOption([value], options)[0];
  return option.disabled;
}
/**
 * `tags` mode should fill un-list item into the option list
 */

export function fillOptionsWithMissingValue(options, value, optionLabelProp, labelInValue) {
  var values = toArray(value).slice().sort();

  var cloneOptions = _toConsumableArray(options); // Convert options value to set


  var optionValues = new Set();
  options.forEach(function (opt) {
    if (opt.options) {
      opt.options.forEach(function (subOpt) {
        optionValues.add(subOpt.value);
      });
    } else {
      optionValues.add(opt.value);
    }
  }); // Fill missing value

  values.forEach(function (item) {
    var val = labelInValue ? item.value : item;

    if (!optionValues.has(val)) {
      var _ref9;

      cloneOptions.push(labelInValue ? (_ref9 = {}, _defineProperty(_ref9, optionLabelProp, item.label), _defineProperty(_ref9, "value", val), _ref9) : {
        value: val
      });
    }
  });
  return cloneOptions;
}
export function customDisplayValues(tmpValues, mergedFlattenOptions, isMultiple, showAll, isSelectAll, displayAllValue) {
  if (isMultiple && showAll && isSelectAll && tmpValues.length > 0) {
    var optValues = mergedFlattenOptions === null || mergedFlattenOptions === void 0 ? void 0 : mergedFlattenOptions.filter(function (v) {
      return !v.data.disabled;
    }).filter(function (v) {
      return !String(v.key).includes('RC_SELECT_GRP');
    }).map(function (item) {
      return String(item.value);
    });

    if (tmpValues.length === optValues.length && tmpValues.every(function (k) {
      return optValues.includes(String(k.value));
    })) {
      return [displayAllValue];
    }
  }
}